import { Field, FormikValues, FormikErrors } from "formik";
import { Autocomplete, TextField } from "@mui/material";

interface QualitySpecificationProps {
  values: FormikValues;
  qualitySpecificationsList: any[];
  setAddQualitySpecificationModalStatus: (show: boolean) => void;
  qltSuggestionList: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<FormikValues>>;
  permissions: any;
}

const QualitySpecificationFields: React.FC<QualitySpecificationProps> = ({
  values,
  setFieldValue,
  qualitySpecificationsList,
  qltSuggestionList,
}) => {
  const selectedIds = values.quality_specification.map(
    (item: any) => item.quality_specification_id,
  );

  const isAllSelected =
    qualitySpecificationsList.length > 0 &&
    selectedIds.length === qualitySpecificationsList.length;

  // Handle checkbox
  const handleCheckboxChange = (e: any, spec: any) => {
    const isChecked = e.target.checked;
    let updatedSpecs = [...values.quality_specification];

    if (isChecked) {
      if (
        !updatedSpecs.some((item) => item.quality_specification_id === spec.id)
      ) {
        updatedSpecs.push({ quality_specification_id: spec.id, value: "" });
      }
    } else {
      updatedSpecs = updatedSpecs.filter(
        (item) => item.quality_specification_id !== spec.id,
      );
    }

    setFieldValue("quality_specification", updatedSpecs);
  };

  // Handle Select All
  const handleSelectAllChange = (e: any) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      const allSpecs = qualitySpecificationsList.map((spec) => ({
        quality_specification_id: spec.id,
        value:
          values.quality_specification.find(
            (item: any) => item.quality_specification_id === spec.id,
          )?.value || "",
      }));
      setFieldValue("quality_specification", allSpecs);
    } else {
      setFieldValue("quality_specification", []);
    }
  };

  // Get current value
  const getValueForSpec = (specId: string) => {
    const spec = values.quality_specification.find(
      (item: any) => item.quality_specification_id === specId,
    );
    return spec ? spec.value : "";
  };

  return (
    <div className="card my-card">
      {/* Header */}
      <div className="card-header my-card-header bg-lighter d-flex justify-content-between align-items-center">
        <h6 className="my-card-header-head m-0">Quality Specifications</h6>

        <div className="my-card-header-checkbox">
          <Field
            type="checkbox"
            name="All_quality_specifications"
            className="form-check-input me-2"
            checked={isAllSelected}
            onChange={handleSelectAllChange}
          />
          <label className="form-check-label">Select All</label>
        </div>
      </div>

      {/* Body */}
      <div className="card-body my-card-body">
        {Array.isArray(qualitySpecificationsList) &&
        qualitySpecificationsList.length > 0 ? (
          <div className="row g-2 m-0">
            {qualitySpecificationsList.map((spec: any) => {
              const isChecked = selectedIds.includes(spec.id);
              const specValue = getValueForSpec(spec.id);

              return (
                <div
                  key={spec.id}
                  className="col-12 d-flex align-items-center mb-2 quality-spec-row"
                  style={{ gap: "1rem" }}
                >
                  {/* Checkbox */}
                  <div
                    className="checkbox-label-group d-flex align-items-start"
                    style={{ width: "250px", minWidth: "200px", flexShrink: 0 }}
                  >
                    <input
                      type="checkbox"
                      id={`spec-${spec.id}`}
                      className="form-check-input mt-1"
                      checked={isChecked}
                      onChange={(e) => handleCheckboxChange(e, spec)}
                    />

                    {/* Label */}
                    <label
                      htmlFor={`spec-${spec.id}`}
                      className="form-check-label fw-semibold ms-1"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2, // max 2 lines
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                        lineHeight: "1.2rem",
                      }}
                    >
                      {spec.name}
                    </label>
                  </div>

                  {/* Value Input (Always visible now) */}
                  <div className="flex-grow-1">
                    <Autocomplete
                      disablePortal
                      freeSolo
                      options={qltSuggestionList[spec.id] || []}
                      value={specValue}
                      onChange={(event, newValue) => {
                        let updatedSpecs = [...values.quality_specification];

                        const existingIndex = updatedSpecs.findIndex(
                          (item: any) =>
                            item.quality_specification_id === spec.id,
                        );

                        if (existingIndex !== -1) {
                          // Update existing
                          updatedSpecs[existingIndex].value = newValue || "";
                        } else {
                          // 🔥 Auto add spec if not selected
                          updatedSpecs.push({
                            quality_specification_id: spec.id,
                            value: newValue || "",
                          });
                        }

                        setFieldValue("quality_specification", updatedSpecs);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter value"
                          value={specValue}
                          onChange={(e) => {
                            let updatedSpecs = [
                              ...values.quality_specification,
                            ];

                            const existingIndex = updatedSpecs.findIndex(
                              (item: any) =>
                                item.quality_specification_id === spec.id,
                            );

                            if (existingIndex !== -1) {
                              updatedSpecs[existingIndex].value =
                                e.target.value;
                            } else {
                              // 🔥 Auto add if not selected
                              updatedSpecs.push({
                                quality_specification_id: spec.id,
                                value: e.target.value,
                              });
                            }

                            setFieldValue(
                              "quality_specification",
                              updatedSpecs,
                            );
                          }}
                          size="small"
                          className="form-control"
                        />
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted m-0">
            No quality specifications available for the selected product.
          </p>
        )}
      </div>
    </div>
  );
};

export default QualitySpecificationFields;
